import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';

export interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: ReactNode;
  children: ReactNode;
  tone?: 'neutral' | 'streak' | 'gem' | 'heart' | 'xp';
}

/**
 * The tone colors the icon, not the number. Gem cyan and XP gold are
 * 1.7:1 against white — fine for a decorative glyph next to a count and an
 * accessible label, unreadable as the count itself.
 */
const ICON_TONES = {
  neutral: 'text-text-2',
  streak: 'text-streak',
  gem: 'text-gem',
  heart: 'text-heart',
  xp: 'text-xp',
} as const;

/** Compact counter used in the top bar (streak, gems, hearts, instrument). */
export function Chip({ icon, children, tone = 'neutral', className, ...rest }: ChipProps) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex h-9 items-center gap-1.5 rounded-full px-2.5',
        'font-bold tabular hover:bg-bg-2 active:scale-95 transition-transform',
        tone === 'neutral' ? 'text-text-2' : 'text-text',
        className,
      )}
      {...rest}
    >
      {icon ? (
        <span aria-hidden className={cn('shrink-0', ICON_TONES[tone])}>
          {icon}
        </span>
      ) : null}
      <span>{children}</span>
    </button>
  );
}
